import { Check, Copy } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { copyText } from '../lib/clipboard'
import { Button } from './Button'
import { toast } from './Toast'

/**
 * Поле с готовой ссылкой и «Копировать». Буфер обмена недоступен — «Не вышло
 * скопировать», а текст в поле выделяется: остаётся «Скопировать» из меню телефона.
 */
export function CopyField({ label, value, extra }: { label: string; value: string; extra?: ReactNode }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const field = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (state === 'idle') return
    const t = setTimeout(() => setState('idle'), 1800)
    return () => clearTimeout(t)
  }, [state])

  const selectAll = () => {
    const el = field.current
    if (!el) return
    el.focus({ preventScroll: true })
    el.select()
    el.setSelectionRange(0, el.value.length)
  }

  const copy = async () => {
    if (await copyText(value)) {
      setState('copied')
      return
    }
    setState('failed')
    toast('Не вышло скопировать — выделите ссылку в поле', { error: true, ms: 3500 })
    selectAll()
  }

  return (
    <div>
      <div className="mb-1 text-xs text-muted">{label}</div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={field}
          readOnly
          value={value}
          aria-label={label}
          onFocus={(e) => e.currentTarget.select()}
          className="h-11 min-w-0 flex-1 basis-48 rounded-lg bg-surface px-3 font-mono text-[13px] text-fg ring-1 ring-line outline-none ring-inset desk:h-8 desk:text-xs"
        />
        <div className="flex gap-2">
          <Button variant={state === 'copied' ? 'secondary' : 'primary'} onClick={() => void copy()} aria-live="polite">
            {state === 'copied' ? <Check size={16} /> : <Copy size={16} />}
            {state === 'copied' ? 'Скопировано' : state === 'failed' ? 'Не вышло' : 'Копировать'}
          </Button>
          {extra}
        </div>
      </div>
    </div>
  )
}
